import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Icons
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DiscountIcon from "@mui/icons-material/Discount";
import FactoryIcon from "@mui/icons-material/Factory";

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
}));

function SaleBillItemView({ item, billCategory }) {
  const formatCurrency = (value) => {
    return `Rs. ${parseFloat(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!item) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No item data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Product Information Header */}
      <Card
        sx={{
          mb: 3,
          bgcolor: alpha(
            billCategory === "RETAIL" ? "#1976d2" : "#9c27b0",
            0.05,
          ),
          border: `1px solid ${alpha(billCategory === "RETAIL" ? "#1976d2" : "#9c27b0", 0.2)}`,
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <InventoryIcon
                fontSize="large"
                color={billCategory === "RETAIL" ? "primary" : "secondary"}
              />
            </Grid>
            <Grid item xs>
              <Typography variant="h6" fontWeight="600">
                {item.productName || "N/A"}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip
                  icon={<LocalOfferIcon />}
                  label={item.barcode || "N/A"}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Qty: ${item.billQty || 0}`}
                  size="small"
                  color={billCategory === "RETAIL" ? "primary" : "secondary"}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Pricing Details */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              <AttachMoneyIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 0.5 }}
              />
              Pricing Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Unit Price
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {formatCurrency(item.pricePerItem)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Quantity
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {item.billQty || 0}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(item.pricePerItem * item.billQty)}
                </Typography>
              </Box>
              {item.discountPerItem > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      <DiscountIcon
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 0.5 }}
                      />
                      Discount
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="error.main"
                    >
                      -{formatCurrency(item.discountPerItem)}
                    </Typography>
                  </Box>
                  <Divider />
                </>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: alpha("#4caf50", 0.1),
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" fontWeight="600">
                  Total Price
                </Typography>
                <Typography variant="h5" fontWeight="700" color="success.main">
                  {formatCurrency(item.price)}
                </Typography>
              </Box>
            </Stack>
          </InfoCard>
        </Grid>

        {/* Profit Analysis */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              <TrendingUpIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 0.5 }}
              />
              Profit Analysis
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Cost per Unit
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {formatCurrency(item.costPerItem)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Cost
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {formatCurrency(item.costPerItem * item.billQty)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Item Profit
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color={item.itemProfit >= 0 ? "success.main" : "error.main"}
                >
                  {formatCurrency(item.itemProfit)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: alpha(
                    item.profitMargin >= 0 ? "#4caf50" : "#f44336",
                    0.1,
                  ),
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" fontWeight="600">
                  Profit Margin
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color={item.profitMargin >= 0 ? "success.main" : "error.main"}
                >
                  {item.profitMargin?.toFixed(2) || 0}%
                </Typography>
              </Box>
            </Stack>
          </InfoCard>
        </Grid>

        {/* Production Batches */}
        {item.saleProductionItems && item.saleProductionItems.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  <FactoryIcon
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mr: 0.5 }}
                  />
                  Production Batches Used
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: alpha(
                            billCategory === "RETAIL" ? "#1976d2" : "#9c27b0",
                            0.1,
                          ),
                        }}
                      >
                        <TableCell>
                          <strong>Production ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Production Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Batch Number</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Available Qty</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Using Qty</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total Cost</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.saleProductionItems.map((batch, index) => (
                        <TableRow
                          key={batch.productionId || index}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha("#000", 0.02),
                            },
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={batch.productionId}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(batch.productionDate)}
                          </TableCell>
                          <TableCell>{batch.batchNumber || "N/A"}</TableCell>
                          <TableCell align="right">
                            {batch.availableQty || 0}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={batch.usingQty}
                              size="small"
                              color={
                                billCategory === "RETAIL"
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(batch.totalCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notes */}
        {item.note && (
          <Grid item xs={12}>
            <InfoCard>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {item.note}
              </Typography>
            </InfoCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default SaleBillItemView;
